import parseAsNumber from '@/utils/parseAsNumber';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useLayoutEffect, useRef } from 'react';
import { isHeadlessBrowser } from '../../utils/isHeadlessBrowser';
/**
 * Adds a staggered fade in animation to a react component. Adds separate animations
 * to the component and its children
 * @param staggerRef - `ref` that we apply the stagger animation to
 * @param options Object containing the following properties:
 *  - triggerRef - `ref` that we use the trigger the animation with. If undefined we use the `staggerRef` to trigger the animation
 *  - childrenStaggerFrom - The direction the children should stagger from. Can be 'start' or 'end'
 *  - delay - The delay in seconds before the animation starts
 *  - childSelector - Select children other than the direct descendants of the `staggerRef`
 */
const useStagger = (staggerRef, options = {}) => {
    const animationRef = useRef(null);
    const hasAnimatedRef = useRef(false);
    const getChildren = (ref) => {
        if (options.childrenAreDataStagger) {
            const children = [...(ref?.querySelectorAll('[data-stagger]') ?? [])];
            children.sort((a, b) => {
                const aOrder = parseAsNumber(a.getAttribute('data-stagger') ?? Number.MAX_SAFE_INTEGER);
                const bOrder = parseAsNumber(b.getAttribute('data-stagger') ?? Number.MAX_SAFE_INTEGER);
                return aOrder - bOrder;
            });
            return children;
        }
        return [...(ref?.children ?? [])];
    };
    useLayoutEffect(() => {
        if (isHeadlessBrowser()) {
            // Avoid running animations during tests due false accessibility violations being flagged
            return;
        }
        if (staggerRef.current) {
            gsap.set(staggerRef.current, { opacity: 0, y: 20 });
            gsap.set(getChildren(staggerRef.current), { opacity: 0, y: 20 });
        }
    }, []);
    useGSAP(() => {
        if (isHeadlessBrowser()) {
            // Avoid running animations during tests due false accessibility violations being flagged
            return;
        }
        const triggerElement = options.triggerRef?.current || staggerRef.current;
        const staggerElement = staggerRef.current;
        if (!triggerElement || !staggerElement || hasAnimatedRef.current) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !hasAnimatedRef.current) {
                    const children = Array.from(getChildren(staggerElement));
                    const tl = gsap.timeline();
                    tl.to(staggerElement, {
                        delay: options.delay ?? 0,
                        y: 0,
                        opacity: 1,
                        duration: 1,
                        ease: 'power3.out',
                    }).to(children, {
                        opacity: 1,
                        y: 0,
                        duration: 0.7,
                        ease: 'power3.out',
                        stagger: {
                            each: 0.3,
                            from: options.childrenStaggerFrom ?? 'start',
                        },
                    }, '<0.2');
                    animationRef.current = tl;
                    hasAnimatedRef.current = true;
                    observer.disconnect();
                }
            });
        }, { threshold: 0 });
        observer.observe(triggerElement);
        return () => {
            observer.disconnect();
            if (animationRef.current) {
                animationRef.current.kill();
            }
        };
    }, {
        scope: staggerRef,
        dependencies: [options.childrenStaggerFrom, options.delay, options.triggerRef?.current],
    });
    return animationRef;
};
export default useStagger;
